import { useCallback, useEffect, useRef, useState } from "react";

import { BillingInvoiceDownload } from "../generated/graphql";
import { captureException } from "../lib/errorHandling";

export const useBillingInvoiceDownload = (
  createDownload: (invoiceId: string) => Promise<BillingInvoiceDownload | null | undefined>
) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const [readyDownload, setReadyDownload] = useState<BillingInvoiceDownload | null>(null);
  const downloadInFlight = useRef(false);
  const pendingWindow = useRef<Window | null>(null);
  const mounted = useRef(true);

  const reset = useCallback(() => {
    setReadyDownload(null);
    setDownloadError(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingWindow.current?.close();
    };
  }, []);

  const download = async (invoiceId: string) => {
    if (downloadInFlight.current) return;
    downloadInFlight.current = true;
    setDownloadingId(invoiceId);
    setDownloadError(false);
    setReadyDownload(null);
    // Reserve the tab during the click so browsers do not block the async
    // download. Remove opener access before navigating to the provider.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    pendingWindow.current = tab;
    try {
      const link = await createDownload(invoiceId);
      if (!link) throw new Error("Missing invoice download");
      if (!mounted.current) {
        tab?.close();
        return;
      }
      if (tab && !tab.closed) tab.location.replace(link.url);
      else setReadyDownload(link);
    } catch (error) {
      captureException(error, "billing/invoices: create invoice download");
      tab?.close();
      if (mounted.current) setDownloadError(true);
    } finally {
      pendingWindow.current = null;
      downloadInFlight.current = false;
      if (mounted.current) setDownloadingId(null);
    }
  };

  return { downloadingId, downloadError, readyDownload, download, reset };
};
